// 📁 src/lightversion/LearningLite.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Chip,
  TextField,
  LinearProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import { useLite } from "./context/LiteContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import LiteDayLayout from "./LiteDayLayout";
import ExperienceItem from "./components/ExperienceItem";

import {
  LEARNING_EXPLORATIONS,
  getLearningContext,
} from "./config/learninglite";

/* --------------------------------------------------
   🔐 CONSTANTS
-------------------------------------------------- */

const STORAGE_KEY = "lite_learning_explorations";

const LearningLite = () => {
  const navigate = useNavigate();
  const { 
  liteJourney,
  markDayComplete,
  entryDecided,
  loading: liteLoading
  } = useLite();

  const { user } = useAuth();

  const completedDays = liteJourney?.completedDays || [];
  const learningContext = getLearningContext(liteJourney);

  /* --------------------------------------------------
     LOCAL STATE (SAFE, LOCAL ONLY)
  -------------------------------------------------- */
  const [reflection, setReflection] = useState("");
  const [saved, setSaved] = useState(false);

  const [activeExploration, setActiveExploration] = useState(null);
  const [answers, setAnswers] = useState({});
  const [insights, setInsights] = useState({});

  /* --------------------------------------------------
     🔒 ENTRY DECISION GUARD
  -------------------------------------------------- */
  useEffect(() => {

  if (liteLoading) return;

  if (!entryDecided && window.location.pathname !== "/lite/welcome") {
    navigate("/lite/welcome",{replace:true});
  }

  }, [entryDecided, liteLoading, navigate]);

  /* --------------------------------------------------
     🔁 DAY ORDER GUARD (DAY 5 REQUIRED)
  -------------------------------------------------- */
  useEffect(() => {
    if (!completedDays.includes(5)) {
      navigate("/lite/market-potential", { replace: true });
    }
  }, [completedDays, navigate]);

  /* --------------------------------------------------
     🔁 LOAD SAVED LEARNING (LOCAL ONLY)
  -------------------------------------------------- */
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
    setAnswers(saved.answers || {});
    setInsights(saved.insights || {});
  }, []);

  const persistLearning = (nextAnswers, nextInsights) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        answers: nextAnswers,
        insights: nextInsights,
      })
    );
  };

  /* --------------------------------------------------
     ▶️ CONTINUE TO DAY 7 (SAFE)
  -------------------------------------------------- */
  const goToDay7 = () => {
    if (!completedDays.includes(6)) {
      markDayComplete();
    }
    navigate("/lite/day-7");
  };

  /* --------------------------------------------------
     🧠 LEARNING HANDLERS
  -------------------------------------------------- */
  const startExploration = (id) => {
    setActiveExploration(id);
  };

  const submitExploration = (exploration) => {
    const responses = answers[exploration.id] || [];
    const insight = exploration.synthesizeInsight(responses);

    const nextInsights = {
      ...insights,
      [exploration.id]: insight,
    };

    persistLearning(answers, nextInsights);
    setInsights(nextInsights);
    setActiveExploration(null);
  };

  return (
    <LiteDayLayout day={6}>
      <Box sx={{ maxWidth: 900, mx: "auto" }}>
        {/* HEADER */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
            Learn Without Pressure
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Short, guided explorations to understand how you think — not tests.
          </Typography>
        </motion.div>

        {/* INTRO */}
        <Box sx={{ mb: 6 }}>
          <Stack spacing={4}>
            <ExperienceItem
              icon="🧠"
              title="Context-aware reflections"
              text="Every question adapts to your goals and dreams.Guiding you toward breakthroughs that matter."
            />
            <ExperienceItem
              icon="🧪"
              title="No scores, no judgment"
              text="Startups don’t win by chasing grades.They win by chasing clarity.This is your safe space to think, refine, and grow - evaluation comes later, impact lasts forever."
            />
            <ExperienceItem
              icon="🌱"
              title="Optional but valuable"
              text="Reflection doesn’t demand hours, only intention.In just minutes, founders uncover truths that guide them.Optional, yes—but within those moments lies value,the spark that shapes tomorrow’s breakthroughs."
            />
          </Stack>
        </Box>

        {/* 🔹 EXPLORATIONS */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography fontWeight="bold" sx={{ mb: 2 }}>
              Guided Self-Explorations (Optional)
            </Typography>

            <Stack spacing={4}>
              {LEARNING_EXPLORATIONS.map((exp) => {
                const prompts = exp.prompts(learningContext);
                const responses = answers[exp.id] || [];
                const completedCount = responses.filter(Boolean).length;
                const progress =
                  Math.round((completedCount / prompts.length) * 100) || 0;

                const nextId = exp.recommendAfter;
                const nextExploration = LEARNING_EXPLORATIONS.find(
                  (e) => e.id === nextId
                );

                return (
                  <Box key={exp.id}>
                    <Typography fontWeight="bold">{exp.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exp.subtitle} · {exp.duration}
                    </Typography>

                    {/* STATUS */}
                    {insights[exp.id] ? (
                      <Chip label="Completed" color="success" sx={{ mt: 1 }} />
                    ) : (
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        onClick={() => startExploration(exp.id)}
                      >
                        Start →
                      </Button>
                    )}

                    {/* ACTIVE EXPLORATION */}
                    {activeExploration === exp.id && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Progress: {completedCount}/{prompts.length}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{ mb: 3 }}
                        />

                        {prompts.map((question, idx) => (
                          <Box key={idx} sx={{ mb: 3 }}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ mb: 1 }}
                            >
                              {idx + 1}. {question}
                            </Typography>

                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              placeholder="Your reflection…"
                              value={responses[idx] || ""}
                              onChange={(e) => {
                                const next = [...responses];
                                next[idx] = e.target.value;
                                const nextAnswers = {
                                  ...answers,
                                  [exp.id]: next,
                                };
                                setAnswers(nextAnswers);
                                persistLearning(nextAnswers, insights);
                              }}
                            />
                          </Box>
                        ))}

                        <Button
                          variant="contained"
                          onClick={() => submitExploration(exp)}
                        >
                          Generate Insight
                        </Button>
                      </Box>
                    )}

                    {/* INSIGHT + PRO TEASER + RECOMMENDATION */}
                    {insights[exp.id] && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          {insights[exp.id]}
                        </Typography>

                        {exp.proTeaser && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1 }}
                          >
                            🔓 {exp.proTeaser}
                          </Typography>
                        )}

                        {nextExploration && !insights[nextExploration.id] && (
                          <Button
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() =>
                              startExploration(nextExploration.id)
                            }
                          >
                            Recommended next: {nextExploration.title} →
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </CardContent>
        </Card>

        {/* OPTIONAL REFLECTION (UNCHANGED) */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                Personal Reflection (Optional)
              </Typography>

              <textarea
                style={{ width: "100%", minHeight: 120 }}
                value={reflection}
                onChange={(e) => {
                  setReflection(e.target.value);
                  setSaved(false);
                }}
              />

              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => setSaved(true)}
                disabled={!reflection.trim()}
              >
                Save Reflection
              </Button>

              {saved && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                  Saved locally.
                </Typography>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <Divider sx={{ my: 4 }} />

        <Box textAlign="center">
          <Button variant="contained" size="large" onClick={goToDay7}>
            Continue to Day 7 →
          </Button>
        </Box>
      </Box>
    </LiteDayLayout>
  );
};

export default LearningLite;
